import { useState, useEffect, useRef } from 'react';
import { toaster } from '../../components/ui/toaster';
import { trackEvent } from './analytics';
import { isIOS } from './platform';

type InstallBrowser =
  | 'brave'
  | 'safari'
  | 'chrome'
  | 'edge'
  | 'firefox'
  | 'samsung'
  | 'other';

const SHARE_ON_IOS = 'Tap the Share button (📤), then "Add to Home Screen"';

/**
 * Install steps follow the gesture each browser actually offers. iPhone Brave
 * has no Add to Home Screen, so it is sent to Safari. iPadOS desktop mode
 * reports a Macintosh user agent; more than one touch point marks that iPad.
 * Desktop Firefox can pin a taskbar window on Windows (143+, or 150+ from the
 * Microsoft Store) and cannot install on Mac or Linux. Safari's Add to Dock
 * needs macOS Sonoma.
 */
function installInstructions(ua: string, isBrave: boolean, maxTouchPoints = 0) {
  const onIOS = isIOS({ userAgent: ua, platform: '', maxTouchPoints });
  const isAndroid = /Android/.test(ua);
  const isEdge = /Edg(?:e|A|iOS)?\//.test(ua);
  const isSamsung = /SamsungBrowser/.test(ua);
  const isFirefox = /Firefox|FxiOS/.test(ua);
  const isChrome = /Chrome|CriOS/.test(ua) && !isBrave && !isEdge && !isSamsung;
  const isSafari =
    /Safari/.test(ua) &&
    !isBrave &&
    !isEdge &&
    !isSamsung &&
    !isFirefox &&
    !/Chrome|CriOS|FxiOS/.test(ua);

  let browser: InstallBrowser = 'other';
  if (isBrave) browser = 'brave';
  else if (isEdge) browser = 'edge';
  else if (isSamsung) browser = 'samsung';
  else if (isFirefox) browser = 'firefox';
  else if (isChrome) browser = 'chrome';
  else if (isSafari) browser = 'safari';

  if (onIOS) return iosInstallCopy(browser, ua);
  if (isAndroid) return androidInstallCopy(browser);
  const os = /Windows/.test(ua)
    ? 'windows'
    : /Macintosh|Mac OS X/.test(ua)
      ? 'mac'
      : 'other';
  return desktopInstallCopy(browser, os, ua);
}

function majorVersion(ua: string, token: RegExp): number | null {
  const match = token.exec(ua);
  if (!match) return null;
  const version = Number(match[1]);
  return Number.isFinite(version) ? version : null;
}

/** Third-party browsers can offer Add to Home Screen from iOS 16.4 on. */
function iosSupportsThirdPartyHomeScreen(ua: string): boolean {
  const match = /(?:iPhone|iPad|CPU) OS (\d+)[_.](\d+)/.exec(ua);
  if (!match) return true;
  const major = Number(match[1]);
  const minor = Number(match[2]);
  return major > 16 || (major === 16 && minor >= 4);
}

function iosInstallCopy(browser: InstallBrowser, ua: string) {
  if (browser === 'brave') {
    return {
      title: 'Install in Safari',
      description:
        'Brave can\'t add this app to your Home Screen. Open this page in Safari, tap Share, then "Add to Home Screen".',
    };
  }
  if (
    (browser === 'chrome' || browser === 'edge' || browser === 'firefox') &&
    !iosSupportsThirdPartyHomeScreen(ua)
  ) {
    return {
      title: 'Install in Safari',
      description:
        'Add to Home Screen from this browser needs iOS 16.4 or later. Open this page in Safari, tap Share, then "Add to Home Screen".',
    };
  }
  const title =
    browser === 'chrome'
      ? 'Install in Chrome'
      : browser === 'edge'
        ? 'Install in Edge'
        : browser === 'firefox'
          ? 'Install in Firefox'
          : browser === 'safari'
            ? 'Install in Safari'
            : 'Install on iOS';
  return { title, description: SHARE_ON_IOS };
}

function androidInstallCopy(browser: InstallBrowser) {
  if (browser === 'brave') {
    return {
      title: 'Install in Brave Browser',
      description:
        'Tap the menu (⋯), then "Add to Home screen" or "Install app".',
    };
  }
  if (browser === 'chrome') {
    return {
      title: 'Install in Chrome',
      description:
        'Tap the menu (⋯), then "Install and create shortcut", then "Install". Older Chrome may say "Install app" or "Add to Home screen".',
    };
  }
  if (browser === 'edge') {
    return {
      title: 'Install in Edge',
      description: 'Tap the menu (⋯), then "Add to phone" or "Install app".',
    };
  }
  if (browser === 'firefox') {
    return {
      title: 'Install in Firefox',
      description: 'Tap the menu (⋯), then "Install".',
    };
  }
  if (browser === 'samsung') {
    return {
      title: 'Install in Samsung Internet',
      description:
        'Tap the install icon in the address bar, or the menu, then "Add page to" and "Home screen".',
    };
  }
  return {
    title: 'Install on Android',
    description:
      'Tap the menu (⋯), then "Install app" or "Add to Home screen".',
  };
}

function desktopInstallCopy(browser: InstallBrowser, os: string, ua: string) {
  if (browser === 'safari') {
    const version = majorVersion(ua, /Version\/(\d+)/);
    if (version !== null && version < 17) {
      return {
        title: 'Install in Safari',
        description: 'Add to Dock needs macOS Sonoma 14 or later.',
      };
    }
    return {
      title: 'Install in Safari',
      description:
        'On macOS Sonoma 14 or later, choose File, then "Add to Dock".',
    };
  }
  if (browser === 'firefox') {
    if (os === 'windows') {
      const version = majorVersion(ua, /Firefox\/(\d+)/);
      if (version !== null && version >= 143) {
        return {
          title: 'Pin in Firefox',
          description:
            'Use the icon in the address bar to pin this site to the taskbar. This needs Firefox 143 or later, or Firefox 150 or later from the Microsoft Store.',
        };
      }
      return {
        title: "Can't pin in this Firefox",
        description:
          'The taskbar pin needs Firefox 143 or later. Microsoft Store installs need Firefox 150 or later.',
      };
    }
    if (os === 'mac') {
      return {
        title: "Can't install in Firefox",
        description:
          "Firefox on Mac can't install this app. Open this page in Safari or Chrome.",
      };
    }
    return {
      title: "Can't install in Firefox",
      description:
        "Firefox can't install this app here. Open this page in Chrome.",
    };
  }
  if (browser === 'brave') {
    return {
      title: 'Install in Brave Browser',
      description:
        'Look for the install icon (↗️) in Brave\'s address bar, or open the menu, then "Save and Share", then "Install page as app".',
    };
  }
  if (browser === 'edge') {
    return {
      title: 'Install in Edge',
      description:
        'Look for the install icon (↗️) in Edge\'s address bar, or open the menu, then "More tools", then "Apps", then "Install this site as an app".',
    };
  }
  if (browser === 'chrome') {
    return {
      title: 'Install in Chrome',
      description:
        'Look for the install icon (↗️) in Chrome\'s address bar, or open the menu, then "Cast, save, and share", then "Install page as app".',
    };
  }
  return {
    title: 'Install Instructions',
    description:
      "Look for the share/install icon (↗️) in your browser's address bar",
  };
}

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isCheckingInstall, setIsCheckingInstall] = useState(true);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const checkIfInstalled = () => {
      try {
        const isStandalone = window.matchMedia(
          '(display-mode: standalone)',
        ).matches;
        const isIOSStandalone = window.navigator.standalone === true;
        const isInApp =
          window.navigator.userAgent.includes('wv') ||
          (window.navigator.userAgent.includes('Mobile') &&
            !window.navigator.userAgent.includes('Safari'));

        const installed = isStandalone || isIOSStandalone || isInApp;
        setIsInstalled(installed);
        setIsCheckingInstall(false);
      } catch (error) {
        console.log('Error checking if installed:', error);
        setIsInstalled(false);
        setIsCheckingInstall(false);
      }
    };

    checkIfInstalled();

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      try {
        e.preventDefault();
        setDeferredPrompt(e);
        deferredPromptRef.current = e;
      } catch (error) {
        console.log('Error handling beforeinstallprompt:', error);
      }
    };

    const handleAppInstalled = () => {
      try {
        setIsInstalled(true);
        setDeferredPrompt(null);
        deferredPromptRef.current = null;
        trackEvent('pwa_installed');
        toaster.create({
          title: 'App installed!',
          description: 'Spades Calculator has been added to your home screen.',
          type: 'success',
          duration: 3000,
        });
      } catch (error) {
        console.log('Error handling appinstalled:', error);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);

      return () => {
        window.removeEventListener(
          'beforeinstallprompt',
          handleBeforeInstallPrompt,
        );
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }
  }, []);

  const handleInstallClick = async () => {
    try {
      const currentDeferredPrompt = deferredPromptRef.current || deferredPrompt;
      if (currentDeferredPrompt && currentDeferredPrompt.prompt) {
        currentDeferredPrompt.prompt();

        if (
          currentDeferredPrompt.userChoice &&
          typeof currentDeferredPrompt.userChoice.then === 'function'
        ) {
          const { outcome } = await currentDeferredPrompt.userChoice;

          if (outcome === 'accepted') {
            toaster.create({
              title: 'Installation started',
              description:
                'Please follow the browser prompts to complete the installation.',
              type: 'success',
              duration: 3000,
            });
          } else {
            toaster.create({
              title: 'Installation cancelled',
              description: 'You can try again anytime from the menu.',
              type: 'info',
              duration: 3000,
            });
          }
        } else {
          toaster.create({
            title: 'Installation prompt shown',
            description:
              'Please follow the browser prompts to complete the installation.',
            type: 'info',
            duration: 3000,
          });
        }

        setDeferredPrompt(null);
        deferredPromptRef.current = null;
        return;
      }

      const isBrave =
        (await navigator.brave?.isBrave()) || /Brave/.test(navigator.userAgent);
      showInstallInstructions(navigator.userAgent, isBrave);
    } catch (error) {
      console.error('Error during installation:', error);
      showInstallInstructions(
        navigator.userAgent,
        /Brave/.test(navigator.userAgent),
      );
    }
  };

  const showInstallInstructions = (ua: string, isBrave: boolean) => {
    const { title, description } = installInstructions(
      ua,
      isBrave,
      navigator.maxTouchPoints,
    );
    toaster.create({
      title,
      description,
      type: 'info',
      duration: 8000,
    });
  };

  return { isInstalled, isCheckingInstall, handleInstallClick };
};
